// src/pages/orders/OrderDetail.tsx
//
// OrderDetail
// - Shows order base info, eSIM list, reservation items (if any), refund actions (admin only).
// - Uses StandardTableFrame for History (events) list.
// - Uses AppPageHeader slots to set a contextual leading title.
// - Memo events can be added/deleted (non-partner only).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add,
  CheckCircleOutline,
  DeleteOutline,
  EditNoteOutlined,
  HelpOutline,
  PaidOutlined,
  Replay,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useRecoilValue } from 'recoil';

import OrderBaseTable from '@components/order/OrderBaseTable';
import EsimListTable from '@components/order/EsimListTable';
import { ReservationItemTable } from '@components/order/ReservationItemTable';
import DangerZoneCard from '@components/common/DangerZoneCard';
import { usePageHeader } from '@components/common/pageHeader.context';
import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import { useOrderDetail } from '@queries/orders/useOrderDetail';
import { useOrderEvents } from '@queries/orders/useOrderEvents';
import { useOrderReservationItems } from '@queries/orders/useOrderReservationItems';

import { addOrderMemo, deleteOrderMemo, type OrderEventResponse } from '@apis/order';
import { userState } from '@recoils/atoms';
import { OrderEventType } from '@models/order';
import { UserGroup } from '@models/user';
import { getOrderEventTypeMeta } from '@constants/orderLabels';
import { KOREAN_TIME_ZONE } from '@constants/timezone';

import { StandardTableFrame, cellSx } from '@components/common/table';

/**
 * Convert an API timestamp to a KST date-time string.
 */
const formatKstDateTime = (input: string | number) => {
  const utcDate = new Date(input);
  const kstDate = utcToZonedTime(utcDate, KOREAN_TIME_ZONE);
  return `${format(kstDate, 'yyyy-MM-dd HH:mm:ss')} KST`;
};

const getOrderEventColor = (eventType?: OrderEventType | string | null) => {
  switch (eventType) {
    case OrderEventType.PROCESSED:
      return 'success.main';
    case OrderEventType.PARTIAL_REFUND:
      return 'warning.main';
    case OrderEventType.FULL_REFUND:
      return 'error.main';
    case OrderEventType.USER_MEMO:
      return 'info.main';
    default:
      return 'text.disabled';
  }
};

const renderOrderEventIcon = (
  eventType?: OrderEventType | string | null,
  color = 'text.disabled',
) => {
  const iconSx = { fontSize: 18, color };

  switch (eventType) {
    case OrderEventType.PROCESSED:
      return <CheckCircleOutline sx={iconSx} />;
    case OrderEventType.PARTIAL_REFUND:
      return <Replay sx={iconSx} />;
    case OrderEventType.FULL_REFUND:
      return <PaidOutlined sx={iconSx} />;
    case OrderEventType.USER_MEMO:
      return <EditNoteOutlined sx={iconSx} />;
    default:
      return <HelpOutline sx={iconSx} />;
  }
};

const OrderEventTypeDisplay = ({
  eventType,
}: {
  eventType?: OrderEventType | string | null;
}) => {
  const meta = getOrderEventTypeMeta(eventType);
  const color = getOrderEventColor(eventType);

  return (
    <Stack
      component="span"
      direction="row"
      alignItems="center"
      spacing={1}
      title={meta.description || String(eventType ?? '')}
      sx={{ minWidth: 0 }}
    >
      <Box
        component="span"
        sx={{
          width: 24,
          height: 24,
          flex: '0 0 24px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {renderOrderEventIcon(eventType, color)}
      </Box>

      <Typography
        component="span"
        variant="body2"
        fontWeight={700}
        sx={{ ...cellSx.nowrap, color }}
      >
        {meta.label}
      </Typography>
    </Stack>
  );
};

const getDisplayText = (value?: string | null) => {
  const text = value?.trim() ?? '';
  return text || '-';
};

const getOrderEventDescription = (event: OrderEventResponse) => {
  const description = getDisplayText(event.description);
  if (description === '-' || description === event.eventType) return '-';
  return description;
};

const getOrderEventDescriptionSx = (event: OrderEventResponse) => {
  const description = getOrderEventDescription(event);
  if (description === '-') {
    return { color: 'text.disabled', fontWeight: 400 };
  }

  switch (event.eventType) {
    case OrderEventType.USER_MEMO:
      return { color: 'text.primary', fontWeight: 500 };
    case OrderEventType.PARTIAL_REFUND:
    case OrderEventType.FULL_REFUND:
      return { color: 'text.primary', fontWeight: 600 };
    default:
      return { color: 'text.secondary', fontWeight: 400 };
  }
};

const OrderDetail = () => {
  const { orderId } = useParams();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const user = useRecoilValue(userState);
  const isPartner = user?.group === UserGroup.PARTNER;

  const { setPageHeaderSlots } = usePageHeader();
  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  const safeOrderId = useMemo(() => {
    const n = Number(orderId);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [orderId]);

  const isValidOrderId = safeOrderId > 0;

  const [memoDraft, setMemoDraft] = useState('');
  const [fullRefundEsim, setFullRefundEsim] = useState(false);

  const { data: orderDetailData, refetch: refetchOrderDetail } = useOrderDetail(isValidOrderId ? safeOrderId : 0);

  const {
    data: orderEventsData,
    refetch: refetchOrderEvents,
    isLoading: isEventsLoading = false,
  } = useOrderEvents(isValidOrderId ? safeOrderId : 0);

  const { data: reservationItemsData, refetch: refetchReservationItems } = useOrderReservationItems(
    isValidOrderId ? safeOrderId : 0,
  );

  const orderDetail = orderDetailData?.data?.[0] ?? null;
  const eventList: OrderEventResponse[] = useMemo(() => {
    const list = orderEventsData?.data ?? [];
    return [...list].sort((a, b) => {
      const createdDiff = Number(b.createdAt ?? 0) - Number(a.createdAt ?? 0);
      if (createdDiff) return createdDiff;
      return Number(b.orderEventId ?? 0) - Number(a.orderEventId ?? 0);
    });
  }, [orderEventsData?.data]);

  const reservationList = reservationItemsData?.data ?? [];
  const isReservationOrder = Array.isArray(reservationList) && reservationList.length > 0;

  const isRefundable = Boolean(orderDetail && orderDetail.paymentAmount > orderDetail.refundAmount);
  const canShowRefund = !isPartner && isRefundable;

  const headerLeading = useMemo(() => {
    if (!orderDetail) return undefined;
    return { title: `Order #${orderDetail.orderCode}` };
  }, [orderDetail]);

  // Header slots: set leading; clear on unmount to avoid leaking.
  useEffect(() => {
    if (!orderDetail) return;

    setPageHeaderSlots({ leading: headerLeading });
    return () => setPageHeaderSlots({});
  }, [headerLeading, orderDetail, setPageHeaderSlots]);

  const refreshAll = useCallback(async () => {
    try {
      await Promise.all([refetchOrderDetail(), refetchReservationItems(), refetchOrderEvents()]);
    } catch {
      // best-effort
    }
  }, [refetchOrderDetail, refetchOrderEvents, refetchReservationItems]);

  const onRefund = useCallback(() => {
    refreshAll();

    // Refresh list pages in the background.
    queryClient.invalidateQueries({ queryKey: ['orders'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['ordersCount'], exact: false });
  }, [queryClient, refreshAll]);

  const handleAddMemo = useCallback(async () => {
    if (!isValidOrderId) return;

    const value = memoDraft.trim();
    if (!value) return;

    try {
      await addOrderMemo(safeOrderId, value);
      systemAlert.success('Memo added.');
      setMemoDraft('');
      refetchOrderEvents();
    } catch {
      systemAlert.error('Failed to add memo.');
    }
  }, [isValidOrderId, memoDraft, refetchOrderEvents, safeOrderId, systemAlert]);

  const handleDeleteEvent = useCallback(
    async (event: OrderEventResponse) => {
      if (!isValidOrderId) return;
      if (isPartner) return;
      if (event.eventType !== OrderEventType.USER_MEMO) {
        systemAlert.error('System events cannot be deleted.');
        return;
      }

      const ok = await confirm({
        title: 'Delete this event?',
        description: 'This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        priority: 'danger',
      });

      if (!ok) return;

      try {
        await deleteOrderMemo(safeOrderId, event.orderEventId);
        systemAlert.success('Event deleted.');
        refetchOrderEvents();
      } catch {
        systemAlert.error('Failed to delete event.');
      }
    },
    [confirm, isPartner, isValidOrderId, refetchOrderEvents, safeOrderId, systemAlert],
  );

  if (!isValidOrderId) return null;
  if (!orderDetail) return null;

  return (
    <Box width="100%">
      <Box mt={{ xs: 2, md: 3 }} />

      <OrderBaseTable order={orderDetail} />

      <Box mt={{ xs: 2, md: 3 }} />

      {orderDetail.memo ? (
        <>
          <TextField
            label="Errors"
            value={orderDetail.memo}
            multiline
            minRows={6}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
          />
          <Box mt={{ xs: 2, md: 3 }} />
        </>
      ) : null}

      <EsimListTable
        isRefundable={isRefundable}
        order={orderDetail}
        fullRefund={fullRefundEsim}
        setFullRefund={setFullRefundEsim}
        onRefund={onRefund}
        hideList={isReservationOrder}
      />

      {isReservationOrder ? (
        <Box mt={{ xs: 2, md: 3 }}>
          <ReservationItemTable reservationItems={reservationList} orderId={orderDetail.orderId} />
        </Box>
      ) : null}

      {canShowRefund ? (
        <Box mt={{ xs: 3, md: 4 }}>
          <DangerZoneCard
            priority="danger"
            title="Full Refund"
            description="This will trigger the full refund flow for this order."
            actions={
              <Button
                variant="contained"
                color="error"
                startIcon={<Replay />}
                onClick={() => setFullRefundEsim(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                REFUND
              </Button>
            }
          />
        </Box>
      ) : null}

      <Box mt={{ xs: 3, md: 4 }} />

      {/* History */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={700}>
              History
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              {eventList.length} event{eventList.length !== 1 ? 's' : ''} in total
            </Typography>
          }
        />

        <CardContent>
          {!isPartner && (
            <Box mb={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label="Add memo"
                  value={memoDraft}
                  onChange={(e) => setMemoDraft(e.target.value)}
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={3}
                  size="small"
                  placeholder="Write a memo"
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' || e.shiftKey) return;
                    e.preventDefault();
                    handleAddMemo();
                  }}
                />
                <Tooltip title="Add memo">
                  <span>
                    <IconButton
                      onClick={handleAddMemo}
                      disabled={!memoDraft.trim() || !isValidOrderId}
                      color="primary"
                    >
                      <Add />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
          )}

          <StandardTableFrame<OrderEventResponse>
            head={
              <TableRow>
                <TableCell width={70} align="right">
                  #
                </TableCell>
                <TableCell width={190}>Date (KST)</TableCell>
                <TableCell width={180}>Event</TableCell>
                <TableCell width={180}>Owner</TableCell>
                <TableCell>Description</TableCell>
                {!isPartner && (
                  <TableCell width={90} align="center">
                    Delete
                  </TableCell>
                )}
              </TableRow>
            }
            rows={eventList}
            getRowKey={(e) => e.orderEventId}
            columnsCount={isPartner ? 5 : 6}
            isLoading={isEventsLoading}
            loadingVariant="skeleton"
            skeletonRows={6}
            emptyText="No events found."
            minWidth={980}
            renderRow={(event, index) => {
              const description = getOrderEventDescription(event);

              return (
                <>
                  <TableCell align="right" sx={cellSx.number}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={cellSx.nowrap}>{formatKstDateTime(event.createdAt)}</TableCell>
                  <TableCell sx={cellSx.nowrap}>
                    <OrderEventTypeDisplay eventType={event.eventType} />
                  </TableCell>
                  <TableCell sx={cellSx.nowrap}>
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: event.owner?.trim() ? 'text.primary' : 'text.disabled' }}
                    >
                      {getDisplayText(event.owner)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={getOrderEventDescriptionSx(event)}
                    >
                      {description}
                    </Typography>
                  </TableCell>

                {!isPartner && (
                  <TableCell align="center" sx={cellSx.nowrap}>
                    <Tooltip
                      title={
                        event.eventType === OrderEventType.USER_MEMO
                          ? 'Delete memo'
                          : 'System events cannot be deleted'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteEvent(event)}
                          aria-label={
                            event.eventType === OrderEventType.USER_MEMO
                              ? 'Delete memo'
                              : 'System event cannot be deleted'
                          }
                          disabled={
                            isEventsLoading || event.eventType !== OrderEventType.USER_MEMO
                          }
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                )}
                </>
              );
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrderDetail;
